import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Button, ToggleButton, ToggleButtonGroup,
  Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody,
  TableSortLabel, CircularProgress, Alert, Chip, Tooltip,
  Divider, Paper,
} from '@mui/material';
import { Download, InfoOutlined } from '@mui/icons-material';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import api from '../../services/api';

// ─── NHS colour palette ───────────────────────────────────────────────────────

const C = {
  blue:      '#005EB8',
  blueLight: '#41B6E6',
  green:     '#009639',
  yellow:    '#FFB81C',
  red:       '#DA291C',
  dark:      '#425563',
  mid:       '#768692',
  pale:      '#E8EDEE',
  white:     '#FFFFFF',
};

// ─── Date preset helpers ──────────────────────────────────────────────────────

function presetDates(preset) {
  const today = new Date();
  const map = {
    '3m':  [subMonths(today, 2),  today],
    '6m':  [subMonths(today, 5),  today],
    '12m': [subMonths(today, 11), today],
  };
  const [s, e] = map[preset] || [];
  if (!s) return null;
  return { start: format(startOfMonth(s), 'yyyy-MM-dd'), end: format(endOfMonth(e), 'yyyy-MM-dd') };
}

// ─── SPC: XmR I-chart ─────────────────────────────────────────────────────────

function calcSPC(values) {
  if (values.length < 2) return null;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const mrBar = values.slice(1).map((v, i) => Math.abs(v - values[i])).reduce((a, b) => a + b, 0) / (values.length - 1);
  const sigmaHat = mrBar / 1.128; // d2 constant for subgroup size 2
  return {
    mean,
    ucl: Math.min(100, +(mean + 3 * sigmaHat).toFixed(2)),
    lcl: Math.max(0,   +(mean - 3 * sigmaHat).toFixed(2)),
    provisional: values.length < 20,
  };
}

// ─── Percentage colour helpers ────────────────────────────────────────────────

function pctFg(pct) {
  if (pct < 5)  return C.green;
  if (pct < 15) return '#7B5800';  // dark amber for readability on yellow bg
  return C.red;
}
function pctBg(pct) {
  if (pct < 5)  return '#E8F5E9';
  if (pct < 15) return '#FFF8E1';
  return '#FFEBEE';
}

// ─── SVG Run Chart ────────────────────────────────────────────────────────────

const VB_W = 900, VB_H = 210;
const PAD  = { top: 20, right: 28, bottom: 52, left: 48 };
const CW   = VB_W - PAD.left - PAD.right;   // 824
const CH   = VB_H - PAD.top  - PAD.bottom;  // 138

function RunChart({ timeSeries }) {
  if (!timeSeries || timeSeries.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, color: C.mid }}>
        <Typography variant="body2">No data for selected period</Typography>
      </Box>
    );
  }

  const n          = timeSeries.length;
  const vacantVals = timeSeries.map(d => d.pct_vacant);
  const lateVals   = timeSeries.map(d => d.pct_incl_late);
  const spc        = calcSPC(vacantVals);

  const rawMax = Math.max(...vacantVals, ...lateVals, spc?.ucl ?? 0, 5);
  const maxY   = Math.ceil(rawMax / 5) * 5;   // round up to next 5%

  const xAt = i => PAD.left + (n > 1 ? (i / (n - 1)) * CW : CW / 2);
  const yAt = v => PAD.top  + CH - (Math.min(v, maxY) / maxY) * CH;

  const makePath = vals =>
    vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');

  // Y grid ticks every 5%
  const yTicks = [];
  for (let v = 0; v <= maxY; v += 5) yTicks.push(v);

  // X labels: "Apr '26"
  const xLabels = timeSeries.map(d => {
    const [yr, mo] = d.period.split('-');
    const dt = new Date(+yr, +mo - 1, 1);
    return { abbr: format(dt, 'MMM'), yr: String(yr).slice(2) };
  });

  return (
    <Box>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', overflow: 'visible' }}>

        {/* Horizontal grid lines */}
        {yTicks.map(v => (
          <g key={v}>
            <line x1={PAD.left} y1={yAt(v)} x2={PAD.left + CW} y2={yAt(v)}
              stroke={v === 0 ? C.dark : C.pale} strokeWidth={v === 0 ? 1 : 0.5} />
            <text x={PAD.left - 6} y={yAt(v) + 4} textAnchor="end" fontSize={9} fill={C.mid}>{v}%</text>
          </g>
        ))}

        {/* SPC control limits */}
        {spc && (
          <>
            <rect x={PAD.left} y={yAt(spc.ucl)} width={CW}
              height={Math.max(0, yAt(spc.lcl) - yAt(spc.ucl))}
              fill="rgba(218,41,28,0.04)" />
            <line x1={PAD.left} y1={yAt(spc.ucl)} x2={PAD.left + CW} y2={yAt(spc.ucl)}
              stroke={C.red} strokeWidth={1} strokeDasharray="4 4" opacity={0.55} />
            {spc.lcl > 0 && (
              <line x1={PAD.left} y1={yAt(spc.lcl)} x2={PAD.left + CW} y2={yAt(spc.lcl)}
                stroke={C.red} strokeWidth={1} strokeDasharray="4 4" opacity={0.55} />
            )}
            {/* Mean */}
            <line x1={PAD.left} y1={yAt(spc.mean)} x2={PAD.left + CW} y2={yAt(spc.mean)}
              stroke={C.dark} strokeWidth={1.5} strokeDasharray="7 4" />
            <text x={PAD.left + CW + 4} y={yAt(spc.mean) + 4} fontSize={8.5} fill={C.dark}>x̄</text>
          </>
        )}

        {/* Late cancellations line (amber) */}
        <path d={makePath(lateVals)} fill="none" stroke={C.yellow} strokeWidth={1.5} strokeDasharray="5 3" opacity={0.85} />

        {/* Main data line (NHS blue) */}
        <path d={makePath(vacantVals)} fill="none" stroke={C.blue} strokeWidth={2.5} strokeLinejoin="round" />

        {/* Data points */}
        {timeSeries.map((d, i) => {
          const isSignal = spc && (d.pct_vacant > spc.ucl || (spc.lcl > 0 && d.pct_vacant < spc.lcl));
          return (
            <circle key={i} cx={xAt(i)} cy={yAt(d.pct_vacant)}
              r={isSignal ? 6 : 4.5}
              fill={isSignal ? C.red : C.blue}
              stroke={C.white} strokeWidth={1.5}>
              <title>{d.period}: {d.pct_vacant}% vacant ({d.vacant} of {d.allocated})</title>
            </circle>
          );
        })}

        {/* X-axis labels */}
        {xLabels.map((lbl, i) => (
          <g key={i}>
            <text x={xAt(i)} y={PAD.top + CH + 16} textAnchor="middle" fontSize={9.5} fill={C.mid}>{lbl.abbr}</text>
            <text x={xAt(i)} y={PAD.top + CH + 27} textAnchor="middle" fontSize={8}   fill={C.pale}>'{lbl.yr}</text>
          </g>
        ))}

        {/* Chart border */}
        <rect x={PAD.left} y={PAD.top} width={CW} height={CH}
          fill="none" stroke={C.pale} strokeWidth={1} />
      </svg>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap', pl: 0.5 }}>
        <LegLine color={C.blue}  label="% Not Utilised" />
        <LegLine color={C.yellow} label="% incl. Late Cancellations" dashed />
        {spc && <LegLine color={C.dark} label={`Mean  x̄ = ${spc.mean.toFixed(1)}%`} dashed />}
        {spc && <LegLine color={C.red}  label={`UCL = ${spc.ucl.toFixed(1)}%`}  dashed />}
        {spc?.provisional && (
          <Chip size="small" label="Provisional limits — fewer than 20 data points"
            sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#FFF8E1', color: '#5D4037' }} />
        )}
      </Box>
    </Box>
  );
}

function LegLine({ color, label, dashed }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <svg width="26" height="12">
        <line x1="0" y1="6" x2="26" y2="6" stroke={color} strokeWidth="2"
          strokeDasharray={dashed ? '5 3' : undefined} />
        {!dashed && <circle cx="13" cy="6" r="3.5" fill={color} stroke="white" strokeWidth="1.5" />}
      </svg>
      <Typography sx={{ fontSize: '0.65rem', color: C.dark }}>{label}</Typography>
    </Box>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function StatCard({ label, primary, secondary, accentColor }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 150, p: 2, borderRadius: 2,
      border: `1.5px solid ${accentColor}30`,
      background: `linear-gradient(135deg, ${accentColor}08 0%, ${C.white} 100%)`,
    }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, color: C.mid, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1, color: accentColor }}>
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: '0.7rem', color: C.mid, mt: 0.5 }}>{secondary}</Typography>
      )}
    </Paper>
  );
}

// ─── Breakdown Table ──────────────────────────────────────────────────────────

const COLS = [
  { id: 'name',               label: 'Name',                  align: 'left'  },
  { id: 'allocated',          label: 'Allocated',             align: 'right' },
  { id: 'vacant',             label: 'Not Utilised',          align: 'right' },
  { id: 'reassigned',         label: 'Reassigned',            align: 'right' },
  { id: 'no_check',           label: 'No Check Submitted',    align: 'right' },
  { id: 'cancelled_under_4w', label: 'Cancelled <4 Wks',      align: 'right' },
  { id: 'pct_vacant',         label: '% Not Utilised',        align: 'right' },
  { id: 'pct_incl_late',      label: '% incl. Late Cancels',  align: 'right' },
];

function BreakdownTable({ rows }) {
  const [sortKey, setSortKey] = useState('pct_vacant');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = key => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0;
    if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === 'asc' ? va - vb : vb - va;
  }), [rows, sortKey, sortDir]);

  if (!rows.length) return (
    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
      No data for this breakdown
    </Typography>
  );

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: C.blue }}>
            {COLS.map(col => (
              <TableCell key={col.id} align={col.align}
                sortDirection={sortKey === col.id ? sortDir : false}
                sx={{ py: 1, whiteSpace: 'nowrap', borderBottom: 'none' }}>
                <TableSortLabel
                  active={sortKey === col.id}
                  direction={sortKey === col.id ? sortDir : 'asc'}
                  onClick={() => handleSort(col.id)}
                  sx={{
                    color: `${C.white} !important`,
                    '& .MuiTableSortLabel-icon': { color: `${C.white} !important`, opacity: 0.65 },
                    fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                  }}>
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, i) => (
            <TableRow key={i} hover sx={{ backgroundColor: i % 2 === 0 ? C.white : '#F7FAFC' }}>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.78rem' }}>{row.name}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{row.allocated.toLocaleString()}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{row.vacant.toLocaleString()}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{row.reassigned.toLocaleString()}</TableCell>
              <TableCell align="right" sx={{
                fontSize: '0.78rem',
                color: row.no_check > 0 ? '#7B5800' : C.mid,
                fontWeight: row.no_check > 0 ? 600 : 400,
              }}>
                {row.no_check.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{
                fontSize: '0.78rem',
                color: row.cancelled_under_4w > 0 ? C.red : C.mid,
                fontWeight: row.cancelled_under_4w > 0 ? 700 : 400,
              }}>
                {row.cancelled_under_4w.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: pctFg(row.pct_vacant), bgcolor: pctBg(row.pct_vacant) }}>
                  {row.pct_vacant}%
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: pctFg(row.pct_incl_late), bgcolor: pctBg(row.pct_incl_late) }}>
                  {row.pct_incl_late}%
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── CSV download ─────────────────────────────────────────────────────────────

function exportCSV(rows, dates) {
  const header = COLS.map(c => c.label).join(',');
  const lines  = rows.map(r => COLS.map(c => `"${r[c.id] ?? ''}"`).join(','));
  const blob   = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href       = url;
  a.download   = `room-utilisation-${dates.start}-${dates.end}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function RoomUtilisationReport() {
  const [preset,      setPreset]      = useState('6m');
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');
  const [isAdHoc,     setIsAdHoc]     = useState(false);
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [rawLoading,  setRawLoading]  = useState(false);
  const [error,       setError]       = useState('');
  const [activeTab,   setActiveTab]   = useState(0);

  const dates = useMemo(() => {
    if (preset !== 'custom') return presetDates(preset);
    return customStart && customEnd ? { start: customStart, end: customEnd } : null;
  }, [preset, customStart, customEnd]);

  const runReport = useCallback(async () => {
    if (!dates) return;
    setLoading(true); setError(''); setData(null);
    try {
      const { data: resp } = await api.get('/reports/room-utilisation', {
        params: { start_date: dates.start, end_date: dates.end, is_ad_hoc: isAdHoc ? 1 : 0 },
      });
      setData(resp);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dates, isAdHoc]);

  const downloadRaw = useCallback(async () => {
    if (!dates) return;
    setRawLoading(true);
    try {
      const { data: rows } = await api.get('/reports/room-utilisation-raw', {
        params: { start_date: dates.start, end_date: dates.end, is_ad_hoc: isAdHoc ? 1 : 0 },
      });
      const cols = [
        { key: 'date',                          label: 'Date' },
        { key: 'day_of_week',                   label: 'Day' },
        { key: 'clinic_name',                   label: 'Clinic' },
        { key: 'clinic_code',                   label: 'Clinic Code' },
        { key: 'session',                       label: 'Session' },
        { key: 'booking_id',                    label: 'Booking ID' },
        { key: 'specialty',                     label: 'Specialty' },
        { key: 'room_number',                   label: 'Room Number' },
        { key: 'room_name',                     label: 'Room Name' },
        { key: 'booking_status',                label: 'Booking Status' },
        { key: 'is_ad_hoc',                     label: 'Ad Hoc' },
        { key: 'check_submitted',               label: 'Check Submitted' },
        { key: 'room_status',                   label: 'Room Status' },
        { key: 'used_by_specialty',             label: 'Used By Specialty' },
        { key: 'clinic_delay',                  label: 'Clinic Delay' },
        { key: 'delay_reason',                  label: 'Delay Reason' },
        { key: 'delay_impacted_another_clinic', label: 'Delay Impacted Another Clinic' },
        { key: 'additional_issues',             label: 'Additional Issues' },
      ];
      const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const header = cols.map(c => escape(c.label)).join(',');
      const lines  = rows.map(r => cols.map(c => escape(r[c.key])).join(','));
      const blob   = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
      const url    = URL.createObjectURL(blob);
      const a      = document.createElement('a');
      a.href       = url;
      a.download   = `room-utilisation-raw-${dates.start}-to-${dates.end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to download raw data');
    } finally {
      setRawLoading(false);
    }
  }, [dates, isAdHoc]);

  const breakdownRows = useMemo(() => {
    if (!data) return [];
    return [data.by_specialty, data.by_clinic, data.by_session][activeTab] || [];
  }, [data, activeTab]);

  const { summary } = data || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: C.blue }}>
          Room Utilisation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Allocation vs utilisation — sourced from room bookings and nurses' daily check submissions.
        </Typography>
      </Box>

      {/* Controls row */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2.5 }}>
        {/* Date presets */}
        <ToggleButtonGroup value={preset} exclusive size="small"
          onChange={(_, v) => v && setPreset(v)}>
          {[['3m','Last 3 months'],['6m','Last 6 months'],['12m','Last 12 months'],['custom','Custom']].map(([v, lbl]) => (
            <ToggleButton key={v} value={v} sx={{ fontSize: '0.72rem', px: 1.5, py: 0.6, textTransform: 'none' }}>
              {lbl}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Custom date range */}
        {preset === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.78rem' }} />
            <Typography variant="caption" color="text.secondary">→</Typography>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.78rem' }} />
          </Box>
        )}

        {/* Standard / Ad Hoc */}
        <ToggleButtonGroup value={isAdHoc ? 'adhoc' : 'std'} exclusive size="small"
          onChange={(_, v) => { if (v) setIsAdHoc(v === 'adhoc'); }}>
          <ToggleButton value="std"   sx={{ fontSize: '0.72rem', px: 1.5, py: 0.6, textTransform: 'none' }}>Standard</ToggleButton>
          <ToggleButton value="adhoc" sx={{ fontSize: '0.72rem', px: 1.5, py: 0.6, textTransform: 'none' }}>Ad Hoc</ToggleButton>
        </ToggleButtonGroup>

        <Button variant="contained" size="small" onClick={runReport}
          disabled={!dates || loading}
          startIcon={loading ? <CircularProgress size={13} color="inherit" /> : null}
          sx={{ bgcolor: C.blue, '&:hover': { bgcolor: '#004494' }, px: 2.5, py: 0.7,
            fontSize: '0.78rem', textTransform: 'none', fontWeight: 700 }}>
          {loading ? 'Loading…' : 'Run Report'}
        </Button>

        {data && (
          <Button variant="outlined" size="small" onClick={downloadRaw}
            disabled={rawLoading}
            startIcon={rawLoading ? <CircularProgress size={13} color="inherit" /> : <Download sx={{ fontSize: '14px !important' }} />}
            sx={{ borderColor: C.blue, color: C.blue, px: 2, py: 0.7,
              fontSize: '0.78rem', textTransform: 'none', fontWeight: 600 }}>
            {rawLoading ? 'Downloading…' : 'Download Raw Data'}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>{error}</Alert>}

      {/* ── Results ── */}
      {summary && (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            <StatCard
              label="Total Allocated"
              primary={summary.allocated.toLocaleString()}
              secondary={`${dates.start} — ${dates.end}`}
              accentColor={C.blue}
            />
            <StatCard
              label="Not Utilised (Vacant)"
              primary={summary.vacant.toLocaleString()}
              secondary={`${summary.pct_vacant}% of allocated`}
              accentColor={summary.pct_vacant < 5 ? C.green : summary.pct_vacant < 15 ? C.yellow : C.red}
            />
            <StatCard
              label="Cancelled < 4 Weeks"
              primary={summary.cancelled_under_4w.toLocaleString()}
              secondary="Non-compliant cancellations"
              accentColor={summary.cancelled_under_4w > 0 ? C.red : C.green}
            />
            <StatCard
              label="% incl. Late Cancellations"
              primary={`${summary.pct_incl_late}%`}
              secondary="Vacant + non-compliant ÷ denominator"
              accentColor={summary.pct_incl_late < 5 ? C.green : summary.pct_incl_late < 15 ? C.yellow : C.red}
            />
          </Box>

          {/* Run chart */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>
                  % Not Utilised — Monthly Run Chart
                </Typography>
                <Typography variant="caption" sx={{ color: C.mid }}>
                  XmR individuals chart  ·  vacant rooms recorded via nurses' daily check
                </Typography>
              </Box>
              {data.time_series.length < 20 && (
                <Tooltip title="SPC control limits are provisional. A minimum of 20 data points is recommended for statistically reliable limits (NHS SPC guidance).">
                  <Chip
                    icon={<InfoOutlined sx={{ fontSize: '11px !important' }} />}
                    label="Provisional SPC limits"
                    size="small"
                    sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#FFF8E1', color: '#5D4037', cursor: 'help' }}
                  />
                </Tooltip>
              )}
            </Box>
            <RunChart timeSeries={data.time_series} />
          </Paper>

          {/* Breakdown table */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: 2, pt: 1, pb: 0 }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{
                minHeight: 38,
                '& .MuiTab-root': { fontSize: '0.75rem', minHeight: 38, py: 0.5, px: 2,
                  textTransform: 'none', fontWeight: 600 },
                '& .Mui-selected':       { color: `${C.blue} !important` },
                '& .MuiTabs-indicator':  { backgroundColor: C.blue },
              }}>
                <Tab label={`By Specialty (${data.by_specialty.length})`} />
                <Tab label={`By Location (${data.by_clinic.length})`} />
                <Tab label="By Session" />
              </Tabs>
              <Button size="small" variant="outlined" startIcon={<Download sx={{ fontSize: '14px !important' }} />}
                onClick={() => exportCSV(breakdownRows, dates)}
                sx={{ borderColor: C.blue, color: C.blue, fontSize: '0.7rem',
                  py: 0.4, px: 1.5, textTransform: 'none' }}>
                CSV
              </Button>
            </Box>
            <Divider />
            <BreakdownTable rows={breakdownRows} />
          </Paper>

          {/* Data limitations */}
          <Alert severity="info" icon={<InfoOutlined sx={{ fontSize: 18 }} />}
            sx={{ fontSize: '0.75rem', '& .MuiAlert-message': { pt: '2px' } }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.25 }}>Data limitations</Typography>
            <Typography sx={{ fontSize: '0.72rem' }}>
              Non-utilisation is derived solely from nurses' daily check submissions where{' '}
              <em>room status = vacant</em>. Rooms without a submitted check appear in the{' '}
              <strong>No Check Submitted</strong> column and are excluded from vacancy calculations —
              meaning actual non-utilisation may be higher than reported.
            </Typography>
          </Alert>
        </>
      )}

      {/* Empty state */}
      {!data && !loading && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: 180, color: C.mid, gap: 1,
          border: `1.5px dashed ${C.pale}`, borderRadius: 2, mt: 1,
        }}>
          <Typography variant="body2">Select a date range and click Run Report</Typography>
          {dates && (
            <Typography variant="caption" sx={{ color: C.pale }}>
              {dates.start} → {dates.end}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
